import React from 'react';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartTheme } from '@modules/ui-components/hooks/useChartTheme';
import type {
  CreativeActualVsPredictedCohortCurve,
  CreativeActualVsPredictedCohortCurvePoint,
} from '../../creativeDecisions';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';
import { formatMinorMoney } from './creativeDecisionFormat';

interface ChartPoint {
  day: number;
  label: string;
  actual?: number;
  p10?: number;
  p50?: number;
  p90?: number;
  source: CreativeActualVsPredictedCohortCurvePoint;
  predictionIntervalValid: boolean;
}

export function CreativeActualVsPredictedCurve({
  curve,
}: {
  curve?: CreativeActualVsPredictedCohortCurve;
}): React.ReactElement {
  const chart = useChartTheme();
  if (!curve) {
    return (
      <Alert severity="info" sx={{ mt: 1.5 }} data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerPredictionCurveState}>
        Actual-versus-predicted cohort evidence is unavailable for this prediction snapshot.
      </Alert>
    );
  }

  const points = normalizePoints(curve);
  const invalidIntervals = points.filter((point) => !point.predictionIntervalValid).length;
  const renderable = points.some((point) => (
    point.actual !== undefined || point.p10 !== undefined || point.p50 !== undefined || point.p90 !== undefined
  ));
  const coverage = curve.coverage
    ? `${curve.coverage.numerator.toLocaleString()} of ${curve.coverage.denominator.toLocaleString()} ${curve.coverage.unit}`
    : 'coverage count unavailable';
  const accessibleSummary = [
    `Actual versus predicted contribution cohort curve in ${curve.currency}.`,
    `${points.length} registered day points.`,
    `Maturity ${curve.maturityState}.`,
    `Coverage ${curve.coverageState}, ${coverage}.`,
  ].join(' ');

  return (
    <Box
      sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerPredictionCurve}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Actual vs predicted cohort contribution</Typography>
          <Typography variant="caption" color="text.secondary">
            {`${curve.valueBasis.replaceAll('_', ' ')} · ${curve.unit.replaceAll('_', ' ')} · as of ${formatDate(curve.asOf)}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          <Chip size="small" variant="outlined" label={curve.maturityState} />
          <Chip
            size="small"
            variant="outlined"
            color={curve.coverageState === 'complete' ? 'success' : curve.coverageState === 'partial' ? 'warning' : 'default'}
            label={`${curve.coverageState} coverage`}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerPredictionCurveState}
          />
        </Stack>
      </Stack>

      {curve.state !== 'available' && (
        <Alert severity={curve.state === 'partial' ? 'warning' : 'info'} sx={{ mt: 1 }}>
          {curve.state === 'partial'
            ? `The curve is partial: ${humanReasons(curve.reasonCodes)}.`
            : `The curve is unavailable: ${humanReasons(curve.reasonCodes)}.`}
        </Alert>
      )}
      {invalidIntervals > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {`${invalidIntervals} prediction interval${invalidIntervals === 1 ? '' : 's'} failed P10 ≤ P50 ≤ P90 validation and were not plotted.`}
        </Alert>
      )}

      {renderable ? (
        <Box role="img" aria-label={accessibleSummary} sx={{ width: '100%', height: { xs: 260, sm: 300 }, mt: 1.25 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="label" tick={chart.axisTick} />
              <YAxis tick={chart.axisTick} tickFormatter={(value: number) => compactMoney(value, curve.currency)} />
              <RechartsTooltip
                formatter={(value) => typeof value === 'number'
                  ? formatMajorMoney(value, curve.currency, curve.currencyExponent)
                  : 'n/a'}
              />
              <Legend />
              <Line type="monotone" dataKey="actual" name="Actual" stroke={chart.positive} strokeWidth={2.5} connectNulls={false} />
              <Line type="monotone" dataKey="p10" name="Predicted P10" stroke={chart.info} strokeDasharray="5 4" connectNulls={false} />
              <Line type="monotone" dataKey="p50" name="Predicted P50" stroke={chart.primary} strokeWidth={2} connectNulls={false} />
              <Line type="monotone" dataKey="p90" name="Predicted P90" stroke={chart.warning} strokeDasharray="5 4" connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Alert severity="info" sx={{ mt: 1 }}>No valid actual or prediction point can be plotted.</Alert>
      )}

      <Stack component="ol" role="list" spacing={0.5} sx={{ m: 0, mt: 1.25, p: 0, listStyle: 'none' }}>
        {points.map((point) => (
          <Box
            component="li"
            key={`${point.day}:${point.source.observedAt ?? ''}`}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerPredictionCurvePoint(point.day)}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '50px repeat(2, minmax(0, 1fr))' },
              gap: 0.75,
              py: 0.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{point.label}</Typography>
            <Typography variant="caption">
              {`Actual ${point.actual === undefined ? point.source.actualState.replaceAll('_', ' ') : formatMajorMoney(point.actual, curve.currency, curve.currencyExponent)}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {point.predictionIntervalValid && point.p50 !== undefined
                ? `Predicted ${formatMajorMoney(point.p50, curve.currency, curve.currencyExponent)} · P10–P90 ${formatOptional(point.p10, curve)}–${formatOptional(point.p90, curve)}`
                : `Prediction ${point.source.predictionState.replaceAll('_', ' ')}`}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {`Coverage: ${coverage}. Observed accumulation is descriptive; prediction intervals are not causal lift.`}
      </Typography>
    </Box>
  );
}

function normalizePoints(curve: CreativeActualVsPredictedCohortCurve): ChartPoint[] {
  return curve.points
    .filter((point) => Number.isFinite(point.cohortAgeDays) && point.cohortAgeDays >= 0)
    .map((point) => {
      const p10 = finite(point.p10AmountMinor);
      const p50 = finite(point.p50AmountMinor);
      const p90 = finite(point.p90AmountMinor);
      const presentInterval = [p10, p50, p90].filter((value) => value !== undefined);
      const predictionIntervalValid = point.predictionState !== 'available'
        || presentInterval.length < 3
        || (p10 as number) <= (p50 as number) && (p50 as number) <= (p90 as number);
      const divisor = 10 ** curve.currencyExponent;
      return {
        day: point.cohortAgeDays,
        label: `D${point.cohortAgeDays}`,
        actual: finite(point.actualAmountMinor) === undefined ? undefined : (point.actualAmountMinor as number) / divisor,
        p10: predictionIntervalValid && p10 !== undefined ? p10 / divisor : undefined,
        p50: predictionIntervalValid && p50 !== undefined ? p50 / divisor : undefined,
        p90: predictionIntervalValid && p90 !== undefined ? p90 / divisor : undefined,
        source: point,
        predictionIntervalValid,
      };
    })
    .sort((left, right) => left.day - right.day);
}

function finite(value: number | undefined): number | undefined {
  return value !== undefined && Number.isFinite(value) ? value : undefined;
}

function formatMajorMoney(value: number, currency: string, exponent: number): string {
  return formatMinorMoney(Math.round(value * 10 ** exponent), currency, exponent);
}

function formatOptional(value: number | undefined, curve: CreativeActualVsPredictedCohortCurve): string {
  return value === undefined ? 'n/a' : formatMajorMoney(value, curve.currency, curve.currencyExponent);
}

function compactMoney(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(0)}`;
  }
}

function humanReasons(reasonCodes: readonly string[]): string {
  return reasonCodes.length ? reasonCodes.map((reason) => reason.replaceAll('_', ' ')).join(' · ') : 'no registered reason';
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed.toLocaleString() : value;
}
